import { useState, Fragment, useRef } from "react";
import { T } from "react-translator-component";
import { Modal, Box, Typography, TextField, InputAdornment, CircularProgress, Avatar } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SubmitButton from "../SubmitButton";
import Separator from "../Separator";
import appServices from "../../services/app-services/appServices";
import { colors } from "../../common/colors";
import {
  convertUserImage,
  getRole,
  isClosedGroup,
  isGroupChat,
  isGroupLeader,
  isMemberRole,
  isSubLeaderRole
} from "../../util/utils";
import axios, { CancelTokenSource } from "axios";

interface AddChatUserListProps {
  open: boolean;
  onClose: () => void;
  onSelect: (options: string) => Promise<boolean>;
  chatInfo: any;
  groupData: any;
  currentUserId?: string | number;
  currentChatUsers?: any;
}

const AddChatUserList: React.FC<AddChatUserListProps> = ({
  open,
  onClose,
  onSelect,
  chatInfo,
  groupData,
  currentUserId,
  currentChatUsers,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchUsers, setSearchUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [trySearch, setTrySearch] = useState<boolean>(false);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const handleChangeSearchTerm = async (value: string) => {
    setSearchTerm(value);

    if (!value) {
      setSearchUsers([])
      return
    }

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenRef.current = axios.CancelToken.source();

    await searchInvitedUsers(value, cancelTokenRef.current.token)
  }

  const searchInvitedUsers = async (term: string, token: any) => {
    const cleanedTerm = term.replace(/^[\s\u3000]+/, '');

    try {
      setLoading(true);
      const groupId = groupData?.id ?? null
      const response = await appServices.searchUsers(cleanedTerm, groupId, token);
      const data = response.users

      setSearchUsers(
        data.sort((a: any, b: any) => {
          const aIsCurrent = currentChatUsers.some((user: any) => user.id === a?.id);
          const bIsCurrent = currentChatUsers.some((user: any) => user.id === b?.id);

          if (aIsCurrent && !bIsCurrent) {
            return -1;
          } else if (!aIsCurrent && bIsCurrent) {
            return 1;
          } else {
            return a?.id - b?.id;
          }
        })
      );
    } catch (error) {
      if (axios.isCancel(error)) {
        // pass
      } else {
        console.error("Error fetching search date:", error);
      }
    } finally {
      setTrySearch(true)
      setLoading(false);
    }
  };

  const fetchGroupRole = (memberId: any) => {
    if (!groupData) return ""

    return groupData.member_group_users.find((groupMember: any) => groupMember.id === memberId)?.group_role
  }

  const addIconBtn = (targetUser: any) => {
    if (currentChatUsers.some((user: any) => user.id === targetUser?.id)) return <></>

    if (targetUser.id === chatInfo?.creator_id) return <></>
    if (targetUser?.isInChat) return <></>

    // グループが閉じていたら追加はできない
    if (groupData && isClosedGroup(groupData?.closed_at)) return <></>

    if (isGroupChat(chatInfo?.chat_type)) {
      const isGroupExecutives =
        groupData &&
        (groupData?.owner?.id === currentUserId || isSubLeaderRole(fetchGroupRole(currentUserId)))

      // グループ全体チャットではリーダーかサブリーダーのみメンバーを追加可能
      if (!isGroupExecutives) return <></>
    }

    return (
      <SubmitButton
        validate={async () => true}
        onSubmit={() => onSelect(targetUser)}
        sx={submit}
      >
        <AddCircleOutlineIcon
          sx={iconStyle(colors.farm_blue200)}
        />
      </SubmitButton>
    );
  };

  const groupRoleFilter = (isMember: boolean) => {
    const groupUsers = [...groupData.member_group_users, groupData.owner]
    const filteredGroupMember = groupUsers.filter((member: any) => {
      return isMember ? isMemberRole(member.group_role) : !isMemberRole(member.group_role)
    })

    const fetchUsers = filteredGroupMember.filter((member: any) => {
      if (searchUsers.some((searchUser: any) => searchUser.id === member.id)) return member
    })

    if (isMember) return fetchUsers

    return fetchUsers.sort((a: any, b: any) => {
      if (isGroupLeader(groupData, a) && !isGroupLeader(groupData, b)) {
        return -1
      } else if (!isGroupLeader(groupData, a) && isGroupLeader(groupData, b)) {
        return 1
      } else if (isSubLeaderRole(a.group_role) && !isSubLeaderRole(b.group_role)) {
        return -1
      } else if (!isSubLeaderRole(a.group_role) && isSubLeaderRole(b.group_role)) {
        return 1
      } else {
        return a.id - b.id
      }
    })
  }

  return (
    <Modal
      sx={modalContainer}
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      slotProps={{ backdrop: { style: modalBackdropStyle } }}
    >
      <Box sx={containerStyle}>
        <Box sx={headerStyle}>
          <Box
            onClick={() => onClose()}
            sx={backButtonStyle}
          >
            <Typography>{T("close")}</Typography>
            <KeyboardArrowDownIcon sx={downIcon}/>
          </Box>
          <Typography sx={searchTitle}>{T("add_chat_user_list_title")}</Typography>
        </Box>

        <Box sx={searchBoxStyle}>
          <Typography sx={instructionStyle}>{T("can_search_for_users")}</Typography>
          <TextField
            sx={textFieldStyle}
            id="outlined-basic"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => handleChangeSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">{loading && <CircularProgress size={20} />}</InputAdornment>,
            }}
          />
        </Box>

        {groupData ? (
          <Box>
            {groupRoleFilter(false).length > 0 && (
              <>
                <Typography sx={sectionTitleStyle}>{T("members_with_roles")}</Typography>
                <Box sx={listContainerStyle}>
                  {groupRoleFilter(false).map((member: any, index: number) => (
                    <Fragment key={`has_role_member_${index}`}>
                      <Box sx={optionContentStyle}>
                        <Avatar sx={userIcon} src={convertUserImage(member?.image)}/>
                        <Typography style={userName}>{member?.nickname || member?.name}</Typography>
                        <Typography variant="body2" color="text.secondary" style={roleText}>
                          &nbsp; {getRole(isGroupLeader(groupData, member) ? "leader" : member?.group_role)}
                        </Typography>
                        {addIconBtn(member)}
                      </Box>
                      <Separator color={colors.farm_gray100} sx={separator} />
                    </Fragment>
                  ))}
                </Box>
              </>
            )}

            {groupRoleFilter(true).length > 0 && (
              <>
                <Typography sx={sectionTitleStyle}>{T("member")}</Typography>
                <Box sx={listContainerStyle}>
                  {groupRoleFilter(true).map((member: any, index: number) => (
                    <Fragment key={`member_${index}`}>
                      <Box sx={optionContentStyle}>
                        <Avatar sx={userIcon} src={convertUserImage(member?.image)}/>
                        <Typography style={userName}>{member?.nickname || member?.name}</Typography>
                        {addIconBtn(member)}
                      </Box>
                      <Separator color={colors.farm_gray100} sx={separator} />
                    </Fragment>
                  ))}
                </Box>
              </>
            )}
          </Box>
        ) : (
          <Box sx={listContainerStyle}>
            {searchUsers.map((searchUser: any, index: number) => (
              <Box sx={optionStyle} key={`searched_${index}`}>
                <Box sx={optionContentStyle}>
                  <Avatar sx={userIcon} src={convertUserImage(searchUser?.image)}/>
                  <Typography style={userName}>{searchUser?.nickname || searchUser?.name}</Typography>
                  {addIconBtn(searchUser)}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {searchUsers.length === 0 && (
          <Typography sx={notFoundStyle}>
            {trySearch && T("add_chat_user_list_not_found")}
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

const modalContainer = {
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  overflow: "auto",
  position: "fixed",
  top: 0,
  left: 0
}
const modalBackdropStyle = {
  backgroundColor: "white"
};
const downIcon = {
  marginTop: "-2px",
  marginLeft: "-3px"
}
const containerStyle = {
  width: "100%",
  bgcolor: "background.paper",
  boxShadow: 24,
  padding: "60px 20px 20px",
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  maxHeight: "100vh",
  overflowY: "auto",
};
const headerStyle = {
  textAlign: "center"
};
const backButtonStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer"
};
const searchBoxStyle = {
  width: "100%",
  marginTop: "10px"
};
const instructionStyle = {
  mb: 1
};
const textFieldStyle = {
  width: "100%"
};
const sectionTitleStyle = {
  textAlign: "left",
  marginTop: "20px"
};
const listContainerStyle = {
  marginBottom: "20px"
};
const optionStyle = {
  width: "100%",
  borderBottom: "1.5px solid",
  borderColor: colors.farm_gray100
};
const optionContentStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 0px"
};
const iconStyle = (color: string) => ({
  color,
  fontSize: "34px",
  cursor: "pointer"
});
const notFoundStyle = {
  paddingTop: "20px"
};
const searchTitle = {
  marginTop: "10px",
  fontWeight: "bold"
}
const userIcon = {
  width: 30,
  height: 30,
  mr: 1
}
const separator = {
  height: "1.5px"
}
const roleText = {
  marginRight: '10px',
  lineHeight: "34px"
}
const userName = {
  flex: 1
}
const submit = {
  minWidth: 0,
  width: "34px",
  height: "34px"
}

export default AddChatUserList